import {
  Card,
  Button,
  Steps,
  Drawer,
  Table,
  Flex,
  Typography,
  Avatar,
} from "antd";
import React, { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { css } from "@emotion/css";
import MoreEmptyState from "../../../../../../Theme/EmptyStates/MoreEmptyStates/MoreEmptyState";
import { getEntityActivityLogsAction } from "../../../../../../store/action/admin/entityActions";
import MaximizeIcon from "../../../../../../Theme/CustomIcons/Icons/Grid/MaximizeIcon";

const { Text } = Typography;

const ActivityLogCard = ({ entityId, auditLogs = [] }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const dispatch = useDispatch();

  // Get activity logs from Redux store
  const activityLogsState = useSelector((state) => state.getEntityActivityLogs);
  const activityLogsData = activityLogsState?.info?.logs || [];
  const activityLogsLoading = activityLogsState?.loading || false;

  // Load activity logs when entityId changes
  useEffect(() => {
    if (entityId) {
      dispatch(getEntityActivityLogsAction(entityId, page, limit));
    }
  }, [entityId, page, limit, dispatch]);

  const activityLogData = useMemo(() => {
    // Use Redux data if available, otherwise fallback to props
    const logsToProcess =
      activityLogsData.length > 0 ? activityLogsData : auditLogs;
    return (logsToProcess || []).slice(0, 3).map((log) => {
      const timestamp = new Date(
        log?.createdAt || log?.timestamp || Date.now()
      );
      const title = log?.action || log?.title || "Updated";
      const description =
        log?.message ||
        log?.description ||
        log?.comment ||
        "Configuration updated";
      const firstName =
        log?.createdBy?.name?.firstName || log?.user?.name?.firstName || "";
      const lastName =
        log?.createdBy?.name?.lastName || log?.user?.name?.lastName || "";
      const initials = `${firstName?.[0] || "S"}${
        lastName?.[0] || "Y"
      }`.toUpperCase();
      return {
        title,
        description,
        timestamp,
        user: {
          initials,
          fullName: `${firstName} ${lastName}`.trim() || "System",
        },
      };
    });
  }, [activityLogsData, auditLogs]);

  const tableData = useMemo(() => {
    // Use Redux data if available, otherwise fallback to props
    const logsToProcess =
      activityLogsData.length > 0 ? activityLogsData : auditLogs;
    return (logsToProcess || []).map((log, idx) => {
      const timestamp = new Date(
        log?.createdAt || log?.timestamp || Date.now()
      );
      const firstName =
        log?.createdBy?.name?.firstName || log?.user?.name?.firstName || "";
      const lastName =
        log?.createdBy?.name?.lastName || log?.user?.name?.lastName || "";
      return {
        key: log?._id || idx,
        date: timestamp.toLocaleDateString(),
        time: timestamp.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        title: log?.action || log?.title || "Updated",
        description:
          log?.message ||
          log?.description ||
          log?.comment ||
          "Configuration updated",
        userName: `${firstName} ${lastName}`.trim() || "System",
      };
    });
  }, [activityLogsData, auditLogs]);

  const columns = [
    { title: "Date", dataIndex: "date", key: "date", width: 140 },
    { title: "Time", dataIndex: "time", key: "time", width: 100 },
    {
      title: "Field name",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>
            {text}
          </Text>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Updated By",
      dataIndex: "userName",
      key: "userName",
      width: 200,
      render: (name) => (
        <Flex align="center" gap={8}>
          <Avatar size={20}>
            {(name || "S").substring(0, 2).toUpperCase()}
          </Avatar>
          <Text style={{ fontSize: 12 }}>{name}</Text>
        </Flex>
      ),
    },
  ];

  if (!activityLogData || activityLogData.length === 0) {
    return (
      <Card
        bordered
        title={
          <Flex
            align="center"
            justify="space-between"
            style={{
              borderBottom: themeDefault?.info?.newConfig?.cardborder,
              width: "100%",
              paddingBottom: 8,
            }}
          >
            Activity Log
          </Flex>
        }
        style={{
          // marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          padding: "16 16 4 16",
        }}
        bodyStyle={{ flex: 1, overflowY: "auto", padding: 16 }}
      >
        <Flex
          align="center"
          className={css`
            height: 100%;
          `}
        >
          <MoreEmptyState
            type="ErrorState"
            size="small"
            title="No activities yet"
          />
        </Flex>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Flex
            align="center"
            justify="space-between"
            style={{
              borderBottom: themeDefault?.info?.newConfig?.cardborder,
              width: "100%",
              paddingBottom: 8,
            }}
          >
            Activity Log
          </Flex>
        }
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "16px 16px 4px 16px",
        }}
        bodyStyle={{ flex: 1, overflowY: "auto", padding: 16 }}
      >
        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 4 }}>
          <Steps
            progressDot
            direction="vertical"
            size="small"
            current={
              activityLogData.length > 0 ? activityLogData.length - 1 : 0
            }
            items={activityLogData.map((item) => ({
              title: (
                <div style={{ marginBottom: 4 }}>
                  <Text strong style={{ fontSize: 14 }}>
                    {item.title} by {item.user?.fullName || "System"}
                  </Text>
                  <div style={{ marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.timestamp.toLocaleDateString()} -{" "}
                      {item.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </div>
                </div>
              ),
              status: "finish",
            }))}
          />
        </div>

        <Button
          type="link"
          onClick={() => setDrawerVisible(true)}
          style={{
            width: "100%",
            marginTop: "auto",
            color: isLightBg ? "#000000" : "#ffffff",
            borderTop: themeDefault?.info?.newConfig?.cardborder,
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "none",
            borderRadius: 0,
            borderWidth: "1px 0 0 0",
          }}
        >
          <MaximizeIcon size="small" />
          View Details
        </Button>
      </Card>

      <Drawer
        title="Activity Log Details"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={1000}
        styles={{ body: { padding: 16 } }}
      >
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={{ position: ["bottomCenter"], showSizeChanger: false }}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Drawer>
    </>
  );
};

export default ActivityLogCard;
